import React, {useState, useEffect} from 'react';
import axios from 'axios';
import {MapPin, Phone, Mail, Building, FileText, Clock, Target, Send, Loader2, CheckCircle, AlertCircle} from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    projectType: ''
  });

  const [status, setStatus] = useState({type: '', message: ''});
  const [isLoading, setIsLoading] = useState(false);
  const [projectTypes, setProjectTypes] = useState([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);

  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/project-types`);
        setProjectTypes(Array.isArray(response.data.data) ? response.data.data : []);
      } catch (error) {
        setProjectTypes([]);
      } finally {
        setIsLoadingTypes(false);
      }
    };

    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/locations`);
        setLocations(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setLocations([]);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchProjectTypes();
    fetchLocations();
  }, []);

  // ✨ Animations anime.js au scroll
  useEffect(() => {
    if (typeof window.anime !== 'function') return;

    const anime = window.anime;

    // Hero Animation
    anime({
      targets: '.pg-contact-hero-content > *',
      opacity: [0, 1],
      translateY: [20, 0],
      delay: anime.stagger(120),
      duration: 1000,
      easing: 'easeOutExpo'
    });

    const handleScroll = () => {
      const elements = document.querySelectorAll('.pg-contact-animate-hide');

      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.92;

        if (isVisible && !el.classList.contains('animated')) {
          el.classList.add('animated');

          anime({
            targets: el,
            opacity: [0, 1],
            translateY: [30, 0],
            duration: 800,
            easing: 'easeOutQuad'
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    setTimeout(handleScroll, 250);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const {name, value} = e.target;
    setFormData(prev => ({...prev, [name]: value}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus({type: '', message: ''});

    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/contacts`, formData);

      if (response.data.success) {
        setStatus({type: 'success', message: 'Message envoyé avec succès !'});
        setFormData({name: '', email: '', phone: '', subject: '', message: '', projectType: ''});
        setTimeout(() => setStatus({type: '', message: ''}), 5000);
      } else {
        setStatus({type: 'error', message: 'Erreur lors de l\'envoi.'});
      }
    } catch (error) {
      setStatus({type: 'error', message: 'Une erreur est survenue.'});
    } finally {
      setIsLoading(false);
    }
  };

  const primaryLocation = locations.find(loc => loc.isPrimary);

  return (
    <div className="pg-contact-root" id="contact">
      {/* Hero Section */}
      <section className="pg-contact-hero">
        <div className="pg-contact-hero-bg"></div>
        <div className="pg-contact-hero-dots"></div>
        <div className="pg-contact-hero-content">
          <h1>Votre projet solaire commence <span>ici même.</span></h1>
          <p>Profitez de l'expertise de Sastra Energy pour vos installations photovoltaïques en Tunisie.</p>
        </div>
      </section>

      {/* Main Container */}
      <div className="pg-contact-wrapper">
        <div className="pg-contact-card">
          {/* Sidebar Information */}
          <div className="pg-contact-sidebar">
            <div className="pg-contact-sidebar-top">
              <h2>Parlons de votre futur.</h2>
              <p>Une question ? Un projet ? Notre équipe d'experts est à votre entière disposition pour vous conseiller.</p>
            </div>

            <div className="pg-contact-info-list">
              <div className="pg-contact-info-item">
                <div className="pg-contact-info-icon"><Mail size={20} /></div>
                <div className="pg-contact-info-text">
                  <h3>Email</h3>
                  <a href="mailto:info@sastraenergy.com">info@sastraenergy.com</a>
                </div>
              </div>

              <div className="pg-contact-info-item">
                <div className="pg-contact-info-icon"><Phone size={20} /></div>
                <div className="pg-contact-info-text">
                  <h3>Appelez-nous</h3>
                  <p>{primaryLocation ? `+216 ${primaryLocation.contact}` : '+216 36 437 473'}</p>
                </div>
              </div>

              <div className="pg-contact-info-item">
                <div className="pg-contact-info-icon"><MapPin size={20} /></div>
                <div className="pg-contact-info-text">
                  <h3>Siège Social</h3>
                  <p>{primaryLocation ? `${primaryLocation.address}, ${primaryLocation.city}` : 'Bardo, Tunis'}</p>
                </div>
              </div>
            </div>

            <div style={{marginTop: 'auto', paddingTop: '40px'}}>
              <div className="pg-contact-info-item">
                <div className="pg-contact-info-icon" style={{background: 'rgba(255,215,0,0.15)', color: 'var(--primary-yellow)'}}><Clock size={20} /></div>
                <div className="pg-contact-info-text">
                  <h3>Disponibilité</h3>
                  <p style={{fontSize: '0.9rem', opacity: 0.8}}>Lundi - Vendredi: 08:30 - 17:30</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form Container */}
          <div className="pg-contact-form-side">
            <div className="pg-contact-form-head">
              <h3>Envoyez-nous un message</h3>
              <p>Réponse garantie sous 24 heures par nos ingénieurs.</p>
            </div>

            <form onSubmit={handleSubmit} className="pg-contact-form">
              <div className="pg-contact-input-field">
                <label>Nom Complet</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Ex: Ahmed Ben Salem" />
              </div>

              <div className="pg-contact-input-field">
                <label>Adresse E-mail</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="prenom@exemple.com" />
              </div>

              <div className="pg-contact-input-field">
                <label>Téléphone</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+216 -- --- ---" />
              </div>

              <div className="pg-contact-input-field">
                <label>Type de Projet</label>
                <select name="projectType" value={formData.projectType} onChange={handleInputChange} disabled={isLoadingTypes}>
                  <option value="">{isLoadingTypes ? 'Chargement...' : 'Choisir une option'}</option>
                  {projectTypes.map(type => <option key={type._id} value={type._id}>{type.label}</option>)}
                </select>
              </div>

              <div className="pg-contact-input-field pg-contact-full-width">
                <label>Sujet du Message</label>
                <input type="text" name="subject" value={formData.subject} onChange={handleInputChange} placeholder="Ex: Devis photovoltaïque résidentiel" />
              </div>

              <div className="pg-contact-input-field pg-contact-full-width">
                <label>Message</label>
                <textarea name="message" value={formData.message} onChange={handleInputChange} required placeholder="Expliquez-nous brièvement votre besoin..."></textarea>
              </div>

              <button type="submit" className="pg-contact-btn-submit" disabled={isLoading}>
                {isLoading ? <Loader2 className="animate-spin" size={20} /> : <><Send size={18} /> Envoyer le message</>}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Locations Section with updated classes */}
      <section className="pg-contact-locations-sec" id="locations">
        <div className="container">
          <div className="pg-contact-loc-header pg-contact-animate-hide">
            <h2>Nos implantations en Tunisie</h2>
            <p>Sastra Energy est à vos côtés partout pour garantir un service de proximité irréprochable.</p>
          </div>

          <div className="pg-contact-loc-grid">
            {isLoadingLocations ? (
              <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '50px'}}>Chargement des bureaux...</div>
            ) : locations.length > 0 ? (
              locations.map((location, index) => (
                <div key={location._id || index} className={`pg-contact-loc-card pg-contact-animate-hide ${location.isPrimary ? 'primary' : ''}`}>
                  {location.isPrimary && <div className="pg-contact-loc-badge">Siège Principal</div>}
                  <div className="pg-contact-loc-icon-wrap">
                    <MapPin size={24} />
                  </div>
                  <h3>{location.city}</h3>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                    <div style={{display: 'flex', gap: '10px', color: 'var(--text-muted)'}}>
                      <Building size={18} style={{color: 'var(--primary-yellow)', flexShrink: 0}} />
                      <span style={{fontSize: '0.95rem'}}>{location.address}</span>
                    </div>
                    <div style={{display: 'flex', gap: '10px', color: 'var(--text-muted)'}}>
                      <Phone size={18} style={{color: 'var(--primary-yellow)', flexShrink: 0}} />
                      <span style={{fontSize: '0.95rem'}}>+216 {location.contact}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="pg-contact-animate-hide" style={{gridColumn: '1/-1', textAlign: 'center', padding: '60px', border: '2px dashed #e2e8f0', borderRadius: '20px'}}>
                <p>Nos bureaux seront bientôt listés. N'hésitez pas à nous appeler.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Success/Error Toasts */}
      {status.message && (
        <div className={`pg-contact-toast ${status.type}`}>
          {status.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {status.message}
        </div>
      )}
    </div>
  );
};

export default Contact;
